import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useNavigate,
} from '@tanstack/react-router';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { Login } from '@/auth/Login';
import { configureApiClient } from '@/api/client';
import { Shell } from '@/app/Shell';
import { DetachedPanel } from '@/panels/DetachedPanel';
import { lazy } from 'react';
import { SystemDateBootstrap } from '@/systemDate/SystemDateBootstrap';
import { useManifestSync } from '@/refdata/useManifestSync';

const TokensPreview = lazy(() => import('@/dev/TokensPreview'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Expose the auth token to the API client without a circular import.
function TokenBridge({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  useEffect(() => {
    configureApiClient(() => token);
  }, [token]);
  return <>{children}</>;
}

// Prefetch the ref-data manifest once and keep it in sync on window focus.
function ManifestSyncMount() {
  useManifestSync();
  return null;
}

function ProtectedIndex() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) void navigate({ to: '/login' });
  }, [user, navigate]);
  if (!user) return null;
  return (
    <SystemDateBootstrap>
      {/* ManifestSyncMount only in the authenticated shell — never on /login or
          in detached panel windows which use ProtectedDetachedPanel instead. */}
      <ManifestSyncMount />
      <Shell />
    </SystemDateBootstrap>
  );
}

function LoginRoute() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user) void navigate({ to: '/' });
  }, [user, navigate]);
  return <Login onSuccess={() => navigate({ to: '/' })} />;
}

function ProtectedDetachedPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) void navigate({ to: '/login' });
  }, [user, navigate]);

  if (!user) return null;

  return (
    <SystemDateBootstrap>
      <DetachedPanel />
    </SystemDateBootstrap>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <AuthProvider>
      <TokenBridge>
        <Outlet />
      </TokenBridge>
    </AuthProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: ProtectedIndex,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
});

const detachedPanelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workbench/panel/$panelId',
  component: ProtectedDetachedPanel,
});

// Dev-only token preview at /dev/tokens. Only registered in DEV builds.
const devTokensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dev/tokens',
  component: TokensPreview,
});

const routeTree = import.meta.env.DEV
  ? rootRoute.addChildren([indexRoute, loginRoute, detachedPanelRoute, devTokensRoute])
  : rootRoute.addChildren([indexRoute, loginRoute, detachedPanelRoute]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
